// =====================================================================
// Prontuario (PME Sprint 0 / S49) — pagina /prontuario no CRM.
// Lista pacientes do prontuario eletronico (EHR) com busca + paginacao.
// Multi-tenant: o backend filtra por companyId do JWT (LGPD).
//
// Skeleton do Sprint 0 — cadastro/detalhe/anamnese/SOAP chegam nos Sprints 1-2.
// Padrao espelhado de pages/RelatoriosDiarios (MUI v4 + layout do CRM).
// =====================================================================
import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  CircularProgress,
  Typography,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputAdornment,
  Box,
  IconButton,
  Tooltip
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { toast } from "react-toastify";

import { listPatients } from "../../services/patientService";
import PatientPreviewDialog from "./PatientPreviewDialog";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    minHeight: 0
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  searchField: { flexGrow: 1, minWidth: 130 },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  },
  pagination: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2)
  }
}));

const PAGE_SIZE = 20;

// 'YYYY-MM-DD' -> 'DD/MM/AAAA'
const formatDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

// Mascara CPF se vier so digitos (11)
const formatCpf = (cpf) => {
  if (!cpf) return "—";
  const d = String(cpf).replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const Prontuario = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  // [S70] busca inicial pode vir da URL (?busca=) — deep-link "Abrir ficha" da agenda
  const initialSearch = (() => {
    try {
      return new URLSearchParams(location.search).get("busca") || "";
    } catch (e) {
      return "";
    }
  })();

  const [patients, setPatients] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchPatients = async ({ search: s, page: p }) => {
    setLoading(true);
    try {
      const data = await listPatients({ search: s, page: p, pageSize: PAGE_SIZE });
      setPatients(Array.isArray(data?.patients) ? data.patients : []);
      setCount(Number(data?.count) || 0);
    } catch (err) {
      toast.error("Falha ao carregar pacientes. Tente novamente.");
      setPatients([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial (imediata) + busca com debounce ao digitar
  useEffect(() => {
    const delay = search ? 450 : 0;
    const t = setTimeout(() => {
      setPage(1);
      fetchPatients({ search, page: 1 });
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.max(Math.ceil(count / PAGE_SIZE), 1);

  const goPage = (next) => {
    const p = Math.min(Math.max(next, 1), totalPages);
    if (p === page) return;
    setPage(p);
    fetchPatients({ search, page: p });
  };

  const handleNovoPaciente = () => {
    history.push("/prontuario/novo");
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Prontuário Médico</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LocalPharmacyIcon />}
            onClick={() => history.push("/prontuario/receituario")}
          >
            Receituário
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNovoPaciente}
          >
            Novo Paciente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por nome, CPF ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Box flexGrow={1} />
          <Typography variant="body2" color="textSecondary">
            {count} {count === 1 ? "paciente" : "pacientes"}
          </Typography>
        </div>

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        ) : patients.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              Nenhum paciente cadastrado ainda
            </Typography>
            <Typography variant="body2">
              {search
                ? "Nenhum resultado para a busca. Tente outro termo."
                : 'Nenhum paciente cadastrado ainda. Clique em "Novo Paciente" para cadastrar o primeiro.'}
            </Typography>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto", width: "100%" }}>
            <Table size="small" style={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Nascimento</TableCell>
                  <TableCell align="center" style={{ width: 70 }}>Ficha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push(`/prontuario/paciente/${p.id}`)}
                  >
                    <TableCell>{p.fullName || "—"}</TableCell>
                    <TableCell>{formatCpf(p.cpf)}</TableCell>
                    <TableCell>{p.phone || "—"}</TableCell>
                    <TableCell>{formatDateBR(p.birthDate)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Pré-visualizar ficha">
                        <IconButton size="small" onClick={() => setPreview(p)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            <div className={classes.pagination}>
              <Button size="small" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                Anterior
              </Button>
              <Typography variant="body2">
                Página {page} de {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page >= totalPages}
                onClick={() => goPage(page + 1)}
              >
                Próxima
              </Button>
            </div>
          </>
        )}
      </Paper>

      <PatientPreviewDialog
        patient={preview}
        open={!!preview}
        onClose={() => setPreview(null)}
        onOpenFull={() => {
          const id = preview && preview.id;
          setPreview(null);
          if (id) history.push(`/prontuario/paciente/${id}`);
        }}
      />
    </MainContainer>
  );
};

export default Prontuario;
